import React, { useState, useRef } from 'react';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Box,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Image as ImageIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import { useDynamicPickitPacks } from '../../hooks/useDynamicPickitPacks';
import { DynamicPickitPack } from '../../types/dynamic-pickit';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import NavPills from 'components/NavPills/NavPills';
import styles from 'assets/jss/material-kit-react/views/profilePage';
import { RequireAuth } from 'components/Auth/RequireAuth';
import BubbleContainer from 'components/BubbleContainer/BubbleContainer';
import config from '../../config/config.json';

const useStyles = makeStyles<Theme>((theme) => ({
  mainContainer: {
    position: 'relative',
    zIndex: 2,
    backgroundColor: theme.palette.background.paper,
    borderRadius: '6px',
    color: theme.palette.text.primary,
  },
  container: {
    ...styles.container,
    position: 'relative',
    zIndex: 3,
  },
  contentContainer: {
    marginLeft: 'auto',
    marginRight: 'auto',
    textAlign: 'center',
    zIndex: 1100,
    '& .MuiTabs-root': {
      zIndex: 1200,
    },
    '& .MuiTab-root': {
      zIndex: 1200,
    },
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  table: {
    minWidth: 650,
  },
  actionCell: {
    width: 120,
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  tagContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  tag: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.spacing(1),
    fontSize: '0.875rem',
  },
  imagePreview: {
    width: '100%',
    height: 200,
    objectFit: 'cover',
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  imageCell: {
    width: 100,
    padding: theme.spacing(1),
  },
  thumbnail: {
    width: 60,
    height: 60,
    objectFit: 'cover',
    borderRadius: theme.spacing(0.5),
  },
  uploadButton: {
    marginLeft: theme.spacing(2),
  },
  fileInput: {
    display: 'none',
  },
}));

interface PackFormData {
  title: string;
  description: string;
  price: string;
  image_path: string;
  entries: { pickit_id: string; amount: string }[];
  tags: string;
}

const initialFormData: PackFormData = {
  title: '',
  description: '',
  price: '',
  image_path: '',
  entries: [{ pickit_id: '', amount: '1' }],
  tags: '',
};

export default function DynamicPickitPacks() {
  const { t } = useTranslation();
  const classes = useStyles();
  const { packs, isLoading, createPack, updatePack, deletePack, uploadPacks } =
    useDynamicPickitPacks();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPack, setEditingPack] = useState<DynamicPickitPack | null>(null);
  const [formData, setFormData] = useState<PackFormData>(initialFormData);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useDocumentTitle(t('staff.dynamicPickitPacks'));

  const handleOpenDialog = (pack?: DynamicPickitPack) => {
    if (pack) {
      setEditingPack(pack);
      setFormData({
        title: pack.title,
        description: pack.description,
        price: pack.price.toString(),
        image_path: pack.image_path,
        entries: pack.entries.map((entry) => ({
          pickit_id: entry.pickit_id.toString(),
          amount: entry.amount.toString(),
        })),
        tags: pack.tags.join(', '),
      });
    } else {
      setEditingPack(null);
      setFormData(initialFormData);
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingPack(null);
    setFormData(initialFormData);
  };

  const handleSubmit = () => {
    const packData = {
      title: formData.title,
      description: formData.description,
      price: parseFloat(formData.price),
      image_path: formData.image_path,
      entries: formData.entries.map((entry) => ({
        pickit_id: parseInt(entry.pickit_id),
        amount: parseInt(entry.amount),
      })),
      tags: formData.tags
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean),
    };

    if (editingPack) {
      updatePack({ id: editingPack.id!, data: packData });
    } else {
      createPack(packData);
    }
    handleCloseDialog();
  };

  const handleAddEntry = () => {
    setFormData((prev) => ({
      ...prev,
      entries: [...prev.entries, { pickit_id: '', amount: '1' }],
    }));
  };

  const handleRemoveEntry = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      entries: prev.entries.filter((_, i) => i !== index),
    }));
  };

  const handleEntryChange = (index: number, field: 'pickit_id' | 'amount', value: string) => {
    setFormData((prev) => ({
      ...prev,
      entries: prev.entries.map((entry, i) => (i === index ? { ...entry, [field]: value } : entry)),
    }));
  };

  if (isLoading) {
    return <Typography>{t('common.loading.fetchingData')}</Typography>;
  }

  return (
    <RequireAuth requiredPermission="is_staff">
      <div>
        <div className={classes.mainContainer}>
          <div className={classes.container}>
            <div className={classes.contentContainer}>
              <NavPills
                queryKey={'t'}
                alignCenter
                fullWidth
                onChange={function () {}}
                color="primary"
                tabs={[
                  {
                    tabName: 'packs',
                    tabButton: t('staff.dynamicPickitPacks'),
                    tabContent: (
                      <>
                        <div className={classes.header}>
                          <Typography variant="h4">{t('staff.dynamicPickitPacks')}</Typography>
                          <Box>
                            <form
                              onSubmit={(e) => {
                                e.preventDefault();
                                const file = fileInputRef.current?.files?.[0];
                                if (file) {
                                  uploadPacks(file);
                                  if (fileInputRef.current) {
                                    fileInputRef.current.value = '';
                                  }
                                }
                              }}
                              style={{ display: 'none' }}
                            >
                              <input
                                type="file"
                                accept=".json"
                                ref={fileInputRef}
                                onChange={(e) => {
                                  if (e.target.files?.[0]) {
                                    e.target.form?.requestSubmit();
                                  }
                                }}
                              />
                            </form>
                            <Button
                              variant="outlined"
                              color="primary"
                              startIcon={<UploadIcon />}
                              onClick={(e) => {
                                e.preventDefault();
                                fileInputRef.current?.click();
                              }}
                              className={classes.uploadButton}
                            >
                              {t('staff.uploadPacks')}
                            </Button>
                            <Button
                              variant="contained"
                              color="primary"
                              startIcon={<AddIcon />}
                              onClick={() => handleOpenDialog()}
                            >
                              {t('staff.createPack')}
                            </Button>
                          </Box>
                        </div>

                        <TableContainer component={Card}>
                          <Table className={classes.table}>
                            <TableHead>
                              <TableRow>
                                <TableCell className={classes.imageCell}>
                                  {t('staff.packImage')}
                                </TableCell>
                                <TableCell>{t('staff.packTitle')}</TableCell>
                                <TableCell>{t('staff.packDescription')}</TableCell>
                                <TableCell>{t('staff.packPrice')}</TableCell>
                                <TableCell>{t('staff.packTags')}</TableCell>
                                <TableCell className={classes.actionCell}>
                                  {t('common.actions')}
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {packs?.map((pack) => (
                                <TableRow key={pack.id}>
                                  <TableCell className={classes.imageCell}>
                                    {pack.image_path && (
                                      <img
                                        src={`${config.backend_base_url}image/${pack.image_path}`}
                                        alt={pack.title}
                                        className={classes.thumbnail}
                                      />
                                    )}
                                  </TableCell>
                                  <TableCell>{pack.title}</TableCell>
                                  <TableCell>{pack.description}</TableCell>
                                  <TableCell>${pack.price.toFixed(2)}</TableCell>
                                  <TableCell>
                                    <div className={classes.tagContainer}>
                                      {pack.tags.map((tag) => (
                                        <span key={tag} className={classes.tag}>
                                          {tag}
                                        </span>
                                      ))}
                                    </div>
                                  </TableCell>
                                  <TableCell className={classes.actionCell}>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleOpenDialog(pack)}
                                      color="primary"
                                    >
                                      <EditIcon />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => deletePack(pack.id!)}
                                      color="error"
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </>
                    ),
                  },
                ]}
              />
            </div>
          </div>
        </div>
      </div>

      <Dialog open={isDialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingPack ? t('staff.editPack') : t('staff.createPack')}</DialogTitle>
        <DialogContent>
          <div className={classes.dialogContent}>
            {formData.image_path && (
              <img
                src={`${config.backend_base_url}image/${formData.image_path}`}
                alt="Pack preview"
                className={classes.imagePreview}
              />
            )}
            <TextField
              label={t('staff.packTitle')}
              value={formData.title}
              onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
              fullWidth
              required
            />
            <TextField
              label={t('staff.packDescription')}
              value={formData.description}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              fullWidth
              multiline
              rows={3}
            />
            <TextField
              label={t('staff.packPrice')}
              value={formData.price}
              onChange={(e) => setFormData((prev) => ({ ...prev, price: e.target.value }))}
              type="number"
              inputProps={{ step: '0.01', min: '0' }}
              fullWidth
              required
            />
            <TextField
              label={t('staff.packImagePath')}
              value={formData.image_path}
              onChange={(e) => setFormData((prev) => ({ ...prev, image_path: e.target.value }))}
              fullWidth
              required
              helperText={t('staff.imagePathHelper')}
              InputProps={{
                startAdornment: <ImageIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
            <TextField
              label={t('staff.packTags')}
              value={formData.tags}
              onChange={(e) => setFormData((prev) => ({ ...prev, tags: e.target.value }))}
              fullWidth
              helperText={t('staff.tagsHelper')}
            />

            <Typography variant="h6">{t('staff.packEntries')}</Typography>
            {formData.entries.map((entry, index) => (
              <Grid container spacing={2} key={index} alignItems="center">
                <Grid item xs={5}>
                  <TextField
                    label={t('staff.pickitId')}
                    value={entry.pickit_id}
                    onChange={(e) => handleEntryChange(index, 'pickit_id', e.target.value)}
                    type="number"
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={5}>
                  <TextField
                    label={t('staff.amount')}
                    value={entry.amount}
                    onChange={(e) => handleEntryChange(index, 'amount', e.target.value)}
                    type="number"
                    inputProps={{ min: '1' }}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={2}>
                  <IconButton
                    color="error"
                    onClick={() => handleRemoveEntry(index)}
                    disabled={formData.entries.length === 1}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
            <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddEntry} fullWidth>
              {t('staff.addEntry')}
            </Button>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('common.cancel')}</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editingPack ? t('common.save') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      <BubbleContainer pageKey="dynamicPickitPacks" helpText={t('help.dynamicPickitPacks')} />
    </RequireAuth>
  );
}
